import { AjaxWrapper, HttpMethod } from './ajaxWrapper'

export interface RSSItem {
  title: string
  link: string
  description: string
  pubDate: string
  author?: string
  category?: string[]
  guid?: string
}

export interface RSSFeed {
  title: string
  description: string
  link: string
  language?: string
  lastBuildDate?: string
  items: RSSItem[]
}

export interface RSSPreviewData {
  feedInfo: RSSFeed
  recentItems: RSSItem[]
  error?: string
}

export class RSSService {
  /**
   * Formats date for display
   */
  public static formatDate(dateString: string): string {
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now.getTime() - date.getTime())
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays === 1) {
        return 'Yesterday'
      } else if (diffDays < 7) {
        return `${diffDays} days ago`
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7)
        return `${weeks} week${weeks > 1 ? 's' : ''} ago`
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      }
    } catch {
      return 'Unknown date'
    }
  }

  /**
   * Extracts domain from URL for display
   */
  public static extractDomain(url: string): string {
    try {
      const urlObj = new URL(url)
      return urlObj.hostname.replace(/^www\./, '')
    } catch {
      return 'Unknown source'
    }
  }

  private static readonly CORS_PROXY = 'https://api.rss2json.com/v1/api.json'
  private static readonly MAX_ITEMS = 5

  /**
   * Fetch RSS feed preview for display in popup
   * @param feedUrl - The RSS feed URL to fetch
   * @returns Promise<RSSPreviewData> - Parsed RSS feed data with preview info
   */
  public async fetchRSSPreview(feedUrl: string): Promise<RSSPreviewData> {
    try {
      // Validate RSS URL
      if (!this.isValidRSSUrl(feedUrl)) {
        throw new Error('Invalid RSS URL format')
      }

      // Use RSS2JSON API to handle CORS and parsing
      const proxyUrl = `${RSSService.CORS_PROXY}?rss_url=${encodeURIComponent(feedUrl)}`

      const response = await fetch(proxyUrl)
      const data = await response.json()

      if (data.status !== 'ok') {
        throw new Error(data.message || 'Failed to fetch RSS feed')
      }

      const feedData = data.feed
      const items = data.items || []

      // Transform the data to our interface
      const feedInfo: RSSFeed = {
        title: feedData.title || 'Unknown Feed',
        description: feedData.description || 'No description available',
        link: feedData.link || feedUrl,
        language: feedData.language,
        lastBuildDate: feedData.lastBuildDate || new Date().toISOString(),
        items: items.map((item: any) => this.transformRSSItem(item)),
      }

      const recentItems = items.slice(0, RSSService.MAX_ITEMS).map((item: any) => this.transformRSSItem(item))

      return {
        feedInfo,
        recentItems,
      }
    } catch (error) {
      // Handle RSS service errors gracefully
      return {
        feedInfo: {
          title: 'Error Loading Feed',
          description: 'Unable to load RSS feed',
          link: feedUrl,
          lastBuildDate: new Date().toISOString(),
          items: [],
        },
        recentItems: [],
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      }
    }
  }

  /**
   * Fetch RSS feed content directly from a feed URL
   * @param feedUrl - The RSS feed URL to fetch
   * @returns Promise<RSSFeed> - Parsed RSS feed data
   */
  public async fetchRSSFeed(feedUrl: string): Promise<RSSFeed> {
    try {
      // Use the existing AjaxWrapper to make the request
      const response = await AjaxWrapper.ajax(feedUrl, HttpMethod.GET, null, 'application/xml')

      // Parse the XML response
      return this.parseRSSXML(response)
    } catch (error) {
      // Handle RSS fetch errors gracefully
      throw new Error(`Failed to fetch RSS feed from ${feedUrl}: ${error.message}`)
    }
  }

  /**
   * Fetch RSS feed content through a proxy service (if CORS is an issue)
   * @param feedUrl - The RSS feed URL to fetch
   * @param proxyUrl - Optional proxy service URL
   * @returns Promise<RSSFeed> - Parsed RSS feed data
   */
  public async fetchRSSFeedWithProxy(feedUrl: string, proxyUrl?: string): Promise<RSSFeed> {
    const proxy = proxyUrl || `${AjaxWrapper.getServerUrl()}/api/rss-proxy`
    const url = `${proxy}?url=${encodeURIComponent(feedUrl)}`

    try {
      const response = await AjaxWrapper.ajax(url, HttpMethod.GET, null)
      return this.parseRSSXML(response)
    } catch (error) {
      // Handle RSS proxy fetch errors gracefully
      throw new Error(`Failed to fetch RSS feed from ${feedUrl}: ${error.message}`)
    }
  }

  /**
   * Fetch multiple RSS feeds in parallel
   * @param feedUrls - Array of RSS feed URLs
   * @returns Promise<RSSFeed[]> - Array of parsed RSS feeds
   */
  public async fetchMultipleRSSFeeds(feedUrls: string[]): Promise<RSSFeed[]> {
    const promises = feedUrls.map(url => this.fetchRSSFeed(url))
    return Promise.all(promises).catch(error => {
      // Handle multiple RSS feed fetch errors gracefully
      return []
    })
  }

  /**
   * Transforms RSS item from API response to our interface
   */
  private transformRSSItem(item: any): RSSItem {
    return {
      title: item.title || 'Untitled',
      description: this.cleanDescription(item.description || item.content || ''),
      link: item.link || '#',
      pubDate: item.pubDate || new Date().toISOString(),
      author: item.author || item.creator,
      category: Array.isArray(item.categories) ? item.categories : item.category ? [item.category] : [],
      guid: item.guid || item.link,
    }
  }

  /**
   * Validates if URL appears to be an RSS feed
   */
  private isValidRSSUrl(url: string): boolean {
    try {
      const urlObj = new URL(url)
      return (
        (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') &&
        (url.includes('rss') ||
          url.includes('feed') ||
          url.includes('xml') ||
          url.endsWith('.rss') ||
          url.endsWith('.xml'))
      )
    } catch {
      return false
    }
  }

  /**
   * Cleans and truncates description text
   */
  private cleanDescription(description: string): string {
    if (!description) return 'No description available'

    // Remove HTML tags
    const cleanText = description.replace(/<[^>]*>/g, '')

    // Decode HTML entities
    const decodedText = cleanText
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, '\'')
      .replace(/&nbsp;/g, ' ')

    // Truncate to reasonable length
    const maxLength = 200
    if (decodedText.length > maxLength) {
      return decodedText.substring(0, maxLength).trim() + '...'
    }

    return decodedText.trim()
  }

  /**
   * Parse RSS XML content into structured data
   * @param xmlContent - Raw XML content from RSS feed
   * @returns RSSFeed - Parsed feed data
   */
  private parseRSSXML(xmlContent: string): RSSFeed {
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlContent, 'text/xml')

    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName('parsererror')
    if (parseError.length > 0) {
      throw new Error('Invalid XML content received from RSS feed')
    }

    const channel = xmlDoc.querySelector('channel')
    if (!channel) {
      throw new Error('No channel element found in RSS feed')
    }

    const items = Array.from(xmlDoc.querySelectorAll('item')).map(item => ({
      title: this.getTextContent(item, 'title') || '',
      link: this.getTextContent(item, 'link') || '',
      description: this.getTextContent(item, 'description') || '',
      pubDate: this.getTextContent(item, 'pubDate') || '',
      author: this.getTextContent(item, 'author') || this.getTextContent(item, 'dc:creator') || undefined,
      category: Array.from(item.querySelectorAll('category')).map(cat => cat.textContent || ''),
      guid: this.getTextContent(item, 'guid') || undefined,
    }))

    return {
      title: this.getTextContent(channel, 'title') || '',
      description: this.getTextContent(channel, 'description') || '',
      link: this.getTextContent(channel, 'link') || '',
      language: this.getTextContent(channel, 'language') || undefined,
      lastBuildDate: this.getTextContent(channel, 'lastBuildDate') || undefined,
      items,
    }
  }

  /**
   * Helper method to safely get text content from XML elements
   */
  private getTextContent(element: Element, tagName: string): string | null {
    const tag = element.querySelector(tagName)
    return tag ? tag.textContent : null
  }
}

// Export singleton instance
export const rssService = new RSSService()
